import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';

export default function AdminStores() {
  const [data, setData] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryForm, setCategoryForm] = useState({ name: '', description: '', is_active: true });
  const [editingCategory, setEditingCategory] = useState(null);
  const load = async () => {
    try {
      const [storesRes, categoriesRes] = await Promise.all([
        api.get('/admin/stores'),
        api.get('/admin/marketplace/categories'),
      ]);
      setData(storesRes.data.stores || []);
      setCategories(categoriesRes.data.categories || []);
    } catch {
      setData([]);
      setCategories([]);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleStatus = async (store, nextStatus) => {
    try {
      await api.patch(`/admin/stores/${store.id}`, { status: nextStatus });
      toast.success('Store updated');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const updateMarketplace = async (store, payload, success = 'Marketplace updated') => {
    try {
      await api.patch(`/admin/stores/${store.id}`, payload);
      toast.success(success);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Update failed');
    }
  };

  const saveCategory = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory?.id) {
        await api.put(`/admin/marketplace/categories/${editingCategory.id}`, categoryForm);
        toast.success('Category updated');
      } else {
        await api.post('/admin/marketplace/categories', categoryForm);
        toast.success('Category added');
      }
      setCategoryForm({ name: '', description: '', is_active: true });
      setEditingCategory(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Category save failed');
    }
  };

  const editCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name || '',
      description: category.description || '',
      is_active: category.is_active !== false,
    });
  };

  const deleteCategory = async (category) => {
    try {
      await api.delete(`/admin/marketplace/categories/${category.id}`);
      toast.success('Category deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Store Management</h1>
        <p className="text-slate-500 text-sm mt-0.5">View stores created by users.</p>
      </div>
      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader><CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Stores</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Username</TableHead>
                <TableHead>Owner</TableHead>
                <TableHead>Package</TableHead>
                <TableHead>Marketplace</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map(store => (
                <TableRow key={store.id}>
                  <TableCell>{store.store_name}</TableCell>
                  <TableCell className="font-mono text-xs">{store.username}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className="font-medium text-slate-900">{store.owner_name || '-'}</div>
                      <div className="text-xs text-slate-500">{store.owner_email || '-'}</div>
                    </div>
                  </TableCell>
                  <TableCell>{store.package?.name || '-'}</TableCell>
                  <TableCell>
                    <div className="text-sm">
                      <div className={`font-medium ${store.listed_in_marketplace ? 'text-emerald-700' : 'text-slate-500'}`}>
                        {store.listed_in_marketplace ? 'Listed' : (store.marketplace_request_status === 'pending' ? 'Pending' : 'Not listed')}
                      </div>
                      <div className="text-xs text-slate-500">{store.marketplace_category || '-'}</div>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-slate-500">
                    {store.featured_until ? new Date(store.featured_until).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>{store.status}</TableCell>
                  <TableCell>{store.currency}</TableCell>
                  <TableCell>{store.created_at ? new Date(store.created_at).toLocaleDateString() : '-'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => toggleStatus(store, store.status === 'disabled' ? 'active' : 'disabled')}>
                        {store.status === 'disabled' ? 'Enable' : 'Disable'}
                      </Button>
                      {!store.listed_in_marketplace ? (
                        <Button size="sm" variant="outline" onClick={() => updateMarketplace(store, { listed_in_marketplace: true, marketplace_request_status: 'approved' }, 'Marketplace listing approved')}>
                          Approve
                        </Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => updateMarketplace(store, { listed_in_marketplace: false, marketplace_request_status: 'disabled' }, 'Marketplace listing disabled')}>
                          Disable listing
                        </Button>
                      )}
                      {store.listed_in_marketplace ? (
                        store.marketplace_featured ? (
                          <Button size="sm" variant="outline" onClick={() => updateMarketplace(store, { featured_until: '' }, 'Store unfeatured')}>
                            Unfeature
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => updateMarketplace(store, { featured_until: new Date(Date.now() + (30 * 24 * 60 * 60 * 1000)).toISOString() }, 'Store featured')}>
                            Feature 30d
                          </Button>
                        )
                      ) : null}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit' }}>Marketplace Categories</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form onSubmit={saveCategory} className="grid gap-4 md:grid-cols-2">
            <div>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Category name"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-sm text-slate-600 h-10 px-3 rounded-xl border border-slate-200 bg-white">
                <input
                  type="checkbox"
                  checked={!!categoryForm.is_active}
                  onChange={(e) => setCategoryForm(prev => ({ ...prev, is_active: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="md:col-span-2">
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Category description"
                rows={3}
              />
            </div>
            <div className="md:col-span-2 flex items-center gap-2">
              <Button type="submit" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                {editingCategory ? 'Update Category' : 'Add Category'}
              </Button>
              {editingCategory && (
                <Button type="button" variant="outline" onClick={() => { setEditingCategory(null); setCategoryForm({ name: '', description: '', is_active: true }); }}>
                  Cancel
                </Button>
              )}
            </div>
          </form>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((category) => (
                  <TableRow key={category.id}>
                    <TableCell>{category.name}</TableCell>
                    <TableCell className="font-mono text-xs">{category.slug}</TableCell>
                    <TableCell>{category.is_active ? 'Active' : 'Disabled'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => editCategory(category)}>
                          Edit
                        </Button>
                        <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteCategory(category)}>
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {categories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-slate-500 py-8">No marketplace categories yet.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
